import React, { Suspense } from "react";
import { ResourceForm } from "src/components/Form/ResourceForm.tsx";
import { useParams, useSearchParams } from "react-router-dom";
import { useModuleRoutePaths } from "src/system/useModuleRoutePaths.ts";
import type { ValidSaveFormResponseData } from "src/components/Form/hooks/useResourceForm/useResourceForm.tsx";
import { replacePathParams } from "@exonn/utils";

import { useModuleTabs } from "src/framework/layout/ModuleTabsProvider.tsx";
import { useDataRouterContext } from "src/lib/react-router/useDataRouterContext.ts";
import { AccessDenied } from "src/components/AccessDenied/AccessDenied.tsx";
import { useModuleCreateDisabled } from "src/system/useModuleCreateDisabled.ts";
import { useModuleEditDisabled } from "src/system/useModuleEditDisabled.ts";
import { FormAction } from "src/components/Form/Form.types.ts";
import { getModuleFirstTabPath } from "src/framework/openModule.ts";
import { useUser } from "src/auth/auth.ts";
import { ResourceEnum } from "src/constants/constants.ts";
import { createModuleRoutePaths } from "src/system/createModuleRoutePaths.ts";
import type {
  CreateAccountingPostingRequestParams,
  CreateStockMoveRequestParams,
} from "src/framework/types.ts";
import { StockMoveQueryParameters as UrlState } from "src/admin/modules/stock-move/routes/create/create.constants.ts";
import { CreateAccountingPostingFormSearchParams } from "src/admin/forms/accounting-posting/contants.ts";
import { useResourceFormHandlers } from "src/system/useResourceFormHandlers.ts";
import { useModule } from "src/system/useModule.tsx";
import { useResourceStorageKeys } from "src/framework/useResourceStorageKeys.tsx";

export type ResourceRouteProps = {
  resource?: string;
  type: FormAction;
  tab?: string;
};

export const ResourceRoute = (props: ResourceRouteProps) => {
  const { type, resource: resourceProp, tab } = props;
  const params = useParams() as {
    id: string;
    resource?: string;
  };

  const [searchParams, setSearchParams] = useSearchParams();

  const resource = resourceProp || params["resource"];

  if (!resource) {
    throw new Error(
      "resource should be passed through props or be inferred from route params",
    );
  }

  const paths = useModuleRoutePaths({
    resource,
  });
  const module = useModule(resource);
  const storageKeys = useResourceStorageKeys({
    resource: resource,
  });
  const storageKey = storageKeys.tabs;
  const { handleSuccess, handleError } = useResourceFormHandlers({
    resource,
    type,
    id: params["id"]!,
    storageKey,
    getTabTitle: module.tabs?.edit?.getTitle,
  });
  const { closeCreateTab, closeEditTab } = useModuleTabs();
  const { data: user } = useUser();

  const { router } = useDataRouterContext();

  const disableCreate = useModuleCreateDisabled(resource);
  const disableEdit = useModuleEditDisabled(resource);

  const handleSaveSuccess = (data: ValidSaveFormResponseData) => {
    if (type === FormAction.Create && disableEdit) {
      // redirect to list if editing is disabled
      const routes = createModuleRoutePaths({
        resource,
      });

      router.navigate(`${routes.root}`).then(() => {
        closeCreateTab();
      });
      return;
    }

    if (type === FormAction.Create) {
      // unfortunately there is such inconsistency in backend responses
      // sometimes backend returns just the id instead of the entire resource
      const id = data?.data?.id ?? data.id;

      router
        .navigate(
          replacePathParams(paths.edit.root, {
            id: id,
          }),
        )
        .then(() => {
          closeCreateTab();
        });
    }
  };

  const handleDeleteSuccess = () => {
    router
      .navigate(
        getModuleFirstTabPath({
          resource,
          user: user!,
        }),
      )
      .then(() => {
        closeEditTab(params);
      });
  };

  const handleResetQueryError = () => {
    if (resource === ResourceEnum.StockMove) {
      searchParams.set(UrlState.SelectSearch, "");
      searchParams.set(UrlState.VariantSku, "");
      setSearchParams(searchParams);
    }
  };

  const requestParams = useResourceFormRequestParams({
    resource,
  });

  if (type === "create" && disableCreate) {
    return <AccessDenied />;
  }

  if (type === "edit" && disableEdit) {
    return <AccessDenied />;
  }

  if (
    searchParams.has(
      CreateAccountingPostingFormSearchParams.ShowAssignOrderForm,
    ) &&
    searchParams.has(CreateAccountingPostingFormSearchParams.BankTransactionId)
  ) {
    return (
      <ResourceForm
        id={
          searchParams.get(
            CreateAccountingPostingFormSearchParams.BankTransactionId,
          )!
        }
        action={FormAction.Edit}
        resource={ResourceEnum.BankTransaction}
      />
    );
  }

  // todo: loading overlay
  return (
    <Suspense
      // fallback={
      //   <LoadingOverlay.Root isLoading>
      //     <LoadingOverlay.Spinner />
      //     <LoadingOverlay.Title>Please wait!</LoadingOverlay.Title>
      //   </LoadingOverlay.Root>
      // }
      fallback={null}
    >
      <ResourceForm
        key={params.id + tab}
        tab={tab}
        id={params.id}
        onUseOneSuccess={handleSuccess}
        onUseOneError={handleError}
        action={type}
        requestParams={requestParams}
        resource={resource}
        onResetQueryError={handleResetQueryError}
        onSaveSuccess={handleSaveSuccess}
        onDeleteSuccess={handleDeleteSuccess}
      />
    </Suspense>
  );
};

const useResourceFormRequestParams = ({ resource }: { resource: string }) => {
  const [searchParams] = useSearchParams();

  if (resource === ResourceEnum.StockMove) {
    const variant_sku = searchParams.get(UrlState.VariantSku) || "";
    return {
      variant_sku,
    } as CreateStockMoveRequestParams;
  }

  if (resource === ResourceEnum.AccountingPosting) {
    const bank_transaction_id = searchParams.get(
      CreateAccountingPostingFormSearchParams.BankTransactionId,
    );
    if (!bank_transaction_id) {
      return undefined;
    }
    return {
      bank_transaction_id,
    } as CreateAccountingPostingRequestParams;
  }

  return undefined;
};
